import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Download, HandHelping } from "lucide-react";
import { getMyTrainerView, requestCoverage } from "@/lib/trainers.functions";
import { saveDeviation } from "@/lib/schedule.functions";
import { PageHeader, EmptyState } from "@/components/AppShell";
import { GoogleCalendarPanel } from "@/components/GoogleCalendarPanel";
import { downloadCsv, formatRange, formatTime, shortDay, toCsv } from "@/lib/format";
import { weeklyMeetingDates } from "@/lib/collab-days";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/my-schedule")({
  head: () => ({
    meta: [
      { title: "My schedule | Leadership Edge Scheduling" },
      { name: "description", content: "Your assigned sessions, teaching blocks, coverage requests, and deviation requests." },
      { property: "og:title", content: "My schedule | Leadership Edge Scheduling" },
      { property: "og:description", content: "Assigned sessions, teaching blocks, and requests." },
    ],
  }),
  component: MySchedule,
});

function MySchedule() {
  const queryClient = useQueryClient();
  const fetchView = useServerFn(getMyTrainerView);
  const askCoverage = useServerFn(requestCoverage);
  const addDeviation = useServerFn(saveDeviation);

  const { data, isPending } = useQuery({ queryKey: ["my-trainer-view"], queryFn: () => fetchView() });

  const [coverFor, setCoverFor] = useState<{ id: string; label: string; meetingLabel: string; note: string } | null>(null);
  const [devForm, setDevForm] = useState<{ period_id: string; start_date: string; end_date: string; reason: string } | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["my-trainer-view"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const coverageMutation = useMutation({
    mutationFn: (payload: { sessionId: string; note: string; meetingLabel: string }) => askCoverage({ data: payload }),
    onSuccess: () => {
      toast.success("Coverage requested.");
      setCoverFor(null);
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deviationMutation = useMutation({
    mutationFn: (payload: { period_id: string; start_date: string; end_date: string; reason: string }) =>
      addDeviation({ data: payload }),
    onSuccess: () => {
      toast.success("Deviation request submitted.");
      setDevForm(null);
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;

  if (!data?.trainerId) {
    return (
      <>
        <PageHeader eyebrow="Trainer" title="My schedule" />
        <GoogleCalendarPanel />
        <EmptyState
          title="No trainer record linked"
          body="Your sign-in email does not match a trainer record yet. Ask an administrator to add you with the same email address."
        />
      </>
    );
  }

  const { blocks, sessions, deviations, periods } = data;

  const meetings = sessions
    .filter((s) => s.status !== "cancelled")
    .flatMap((s) => {
      const stored = Array.isArray(s.meeting_dates)
        ? (s.meeting_dates as unknown[]).filter((d): d is string => typeof d === "string")
        : [];
      const dates = stored.length > 0 ? [...stored].sort() : weeklyMeetingDates(s.start_date, s.end_date);
      return dates.map((date, i) => ({
        session: s,
        date,
        number: i + 1,
        total: dates.length,
        label: `Session ${i + 1} · ${date}`,
      }));
    })
    .sort((a, b) => (a.date + a.session.start_time).localeCompare(b.date + b.session.start_time));

  function exportMine() {
    const rows: (string | number | null)[][] = [
      ["Course", "Timeframe", "Start date", "End date", "Start time", "End time", "Status", "Enrolled"],
      ...sessions.map((s) => [
        s.courses?.name ?? "",
        s.schedule_periods?.name ?? "",
        s.start_date,
        s.end_date,
        s.start_time,
        s.end_time,
        s.status,
        s.enrolled_count ?? 0,
      ]),
    ];
    downloadCsv("my-schedule.csv", toCsv(rows));
  }

  return (
    <>
      <PageHeader
        eyebrow="Trainer"
        title="My schedule"
        description="Sessions assigned to you, plus the teaching blocks and requests behind them."
        actions={
          <>
            <Button variant="soft" onClick={exportMine} disabled={sessions.length === 0}>
              <Download /> Export CSV
            </Button>
            <Button
              variant="brass"
              onClick={() =>
                setDevForm({
                  period_id: periods[0]?.id ?? "",
                  start_date: new Date().toISOString().slice(0, 10),
                  end_date: new Date().toISOString().slice(0, 10),
                  reason: "",
                })
              }
              disabled={periods.length === 0}
            >
              Request time away
            </Button>
          </>
        }
      />

      <GoogleCalendarPanel />

      <section className="panel mb-8 p-5">
        <h2 className="font-display text-xl text-foreground">My teaching blocks</h2>
        {blocks.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            No teaching blocks yet. An administrator sets these; sessions can only be assigned inside them.
          </p>
        ) : (
          <ul className="mt-4 flex flex-wrap gap-2">
            {blocks.map((block) => (
              <li key={block.id} className="rounded-md border border-border bg-surface px-3 py-2 text-sm">
                <span className="font-mono text-xs text-accent">{shortDay(block.day_of_week)}</span>{" "}
                {formatTime(block.start_time)}–{formatTime(block.end_time)}
                {block.label ? <span className="text-muted-foreground"> · {block.label}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mb-8">
        <h2 className="mb-3 font-display text-xl text-foreground">Assigned sessions</h2>
        {meetings.length === 0 ? (
          <EmptyState title="Nothing assigned yet" body="Once a schedule is built and deployed, your sessions appear here." />
        ) : (
          <div className="panel divide-y divide-border">
            {meetings.map(({ session, date, number, total, label }) => {
              const requested =
                session.coverage_requested &&
                (session.coverage_note ?? "").includes(`[${label}]`);
              const legacyRequested =
                session.coverage_requested && !(session.coverage_note ?? "").includes("[");
              return (
                <div key={`${session.id}-${number}`} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div>
                    <p className="font-medium text-foreground">
                      {session.courses?.name ?? "Course"}
                      <span className="text-muted-foreground"> · Session {number} of {total}</span>
                    </p>
                    <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                      {formatRange(date, date)} · {formatTime(session.start_time)}–{formatTime(session.end_time)}
                      {session.schedule_periods?.name ? ` · ${session.schedule_periods.name}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {session.enrolled_count ? (
                      <span className="rounded-full bg-success/12 px-2.5 py-1 text-xs font-medium text-success">
                        {session.enrolled_count} enrolled
                      </span>
                    ) : null}
                    {requested || legacyRequested ? (
                      <span className="rounded-full bg-warning/15 px-2.5 py-1 text-xs font-medium text-warning-foreground">
                        Coverage requested
                      </span>
                    ) : (
                      <Button
                        variant="soft"
                        size="sm"
                        onClick={() =>
                          setCoverFor({
                            id: session.id,
                            meetingLabel: label,
                            label: `${session.courses?.name ?? "Course"} · Session ${number} · ${formatRange(date, date)}`,
                            note: "",
                          })
                        }
                      >
                        <HandHelping /> Request coverage
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 font-display text-xl text-foreground">My requests</h2>
        {deviations.length === 0 ? (
          <p className="text-sm text-muted-foreground">No deviation requests submitted.</p>
        ) : (
          <div className="panel divide-y divide-border">
            {deviations.map((request) => (
              <div key={request.id} className="flex items-center justify-between gap-3 p-4">
                <div>
                  <p className="font-mono text-xs text-muted-foreground">
                    {formatRange(request.start_date, request.end_date)}
                  </p>
                  {request.reason ? <p className="mt-1 text-sm text-foreground">{request.reason}</p> : null}
                </div>
                <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium capitalize text-muted-foreground">
                  {request.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <Dialog open={coverFor !== null} onOpenChange={(open) => !open && setCoverFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request coverage</DialogTitle>
            <DialogDescription>{coverFor?.label}</DialogDescription>
          </DialogHeader>
          {coverFor ? (
            <div className="space-y-1.5">
              <Label htmlFor="cover-note">Note for the scheduler</Label>
              <Textarea
                id="cover-note"
                rows={3}
                value={coverFor.note}
                onChange={(e) => setCoverFor({ ...coverFor, note: e.target.value })}
              />
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="soft" onClick={() => setCoverFor(null)}>
              Cancel
            </Button>
            <Button
              variant="brass"
              disabled={coverageMutation.isPending}
              onClick={() => coverFor && coverageMutation.mutate({ sessionId: coverFor.id, note: coverFor.note, meetingLabel: coverFor.meetingLabel })}
            >
              Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={devForm !== null} onOpenChange={(open) => !open && setDevForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request time away</DialogTitle>
            <DialogDescription>Approved requests keep you off the schedule for those dates.</DialogDescription>
          </DialogHeader>
          {devForm ? (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Timeframe</Label>
                <Select value={devForm.period_id} onValueChange={(value) => setDevForm({ ...devForm, period_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a timeframe" />
                  </SelectTrigger>
                  <SelectContent>
                    {periods.map((period) => (
                      <SelectItem key={period.id} value={period.id}>
                        {period.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="my-dev-start">From</Label>
                  <Input
                    id="my-dev-start"
                    type="date"
                    value={devForm.start_date}
                    onChange={(e) => setDevForm({ ...devForm, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="my-dev-end">To</Label>
                  <Input
                    id="my-dev-end"
                    type="date"
                    value={devForm.end_date}
                    onChange={(e) => setDevForm({ ...devForm, end_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="my-dev-reason">Reason</Label>
                <Textarea
                  id="my-dev-reason"
                  rows={3}
                  value={devForm.reason}
                  onChange={(e) => setDevForm({ ...devForm, reason: e.target.value })}
                />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="soft" onClick={() => setDevForm(null)}>
              Cancel
            </Button>
            <Button
              variant="brass"
              disabled={deviationMutation.isPending || !devForm?.period_id}
              onClick={() => devForm && deviationMutation.mutate(devForm)}
            >
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
